import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ── Heroicons — outline (default) ──────────────────────────────
import {
  GlobeAltIcon,
  BoltIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  CodeBracketIcon,
  CloudIcon,
  Square3Stack3DIcon,
  ComputerDesktopIcon,
  SignalIcon,
  BriefcaseIcon,
  BookOpenIcon,
  UsersIcon,
  DocumentTextIcon,
  LockClosedIcon,
  EnvelopeIcon,
  PhoneIcon,
  ServerStackIcon,
  ArrowTrendingUpIcon,
  CreditCardIcon,
  ShoppingCartIcon,
  TruckIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
  ChevronRightIcon,
  BeakerIcon,
  CpuChipIcon,
  FingerPrintIcon,
  NewspaperIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
  CurrencyDollarIcon,
  EyeSlashIcon,
  ClockIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChartPieIcon,
} from "@heroicons/react/24/outline";

import {
  GlobeAltIcon         as GlobeAltSolid,
  BoltIcon             as BoltSolid,
  ChartBarIcon         as ChartBarSolid,
  ShieldCheckIcon      as ShieldCheckSolid,
  DevicePhoneMobileIcon as DevicePhoneMobileSolid,
  CodeBracketIcon      as CodeBracketSolid,
  CloudIcon            as CloudIconSolid,
  Square3Stack3DIcon   as Square3Stack3DSolid,
  ComputerDesktopIcon  as ComputerDesktopSolid,
  SignalIcon           as SignalSolid,
  BriefcaseIcon        as BriefcaseSolid,
  BookOpenIcon         as BookOpenSolid,
  UsersIcon            as UsersSolid,
  DocumentTextIcon     as DocumentTextSolid,
  LockClosedIcon       as LockClosedSolid,
  EnvelopeIcon         as EnvelopeSolid,
  PhoneIcon            as PhoneSolid,
  ServerStackIcon      as ServerStackSolid,
  ArrowTrendingUpIcon  as ArrowTrendingUpSolid,
  CreditCardIcon       as CreditCardSolid,
  ShoppingCartIcon     as ShoppingCartSolid,
  TruckIcon            as TruckSolid,
  CalendarDaysIcon     as CalendarDaysSolid,
  CheckCircleIcon      as CheckCircleSolid,
  ArchiveBoxIcon       as ArchiveBoxSolid,
  BeakerIcon           as BeakerSolid,
  CpuChipIcon          as CpuChipSolid,
  FingerPrintIcon      as FingerPrintSolid,
  NewspaperIcon        as NewspaperSolid,
  PencilSquareIcon     as PencilSquareSolid,
  RocketLaunchIcon     as RocketLaunchSolid,
  CurrencyDollarIcon   as CurrencyDollarSolid,
  ExclamationTriangleIcon as ExclamationTriangleSolid,
  EyeSlashIcon         as EyeSlashSolid,
  ClockIcon            as ClockSolid,
  BuildingOfficeIcon   as BuildingOfficeSolid,
  SparklesIcon         as SparklesSolid,
  ArrowPathIcon        as ArrowPathSolid,
  MagnifyingGlassIcon  as MagnifyingGlassSolid,
  ChartPieIcon         as ChartPieSolid,
} from "@heroicons/react/24/solid";

import "./header.css";
import CloudisianLogo from "../../images/cloudisianIcon.png";

const AppIcon  = CodeBracketIcon;  const AppSolid  = CodeBracketSolid;
const ResearchIcon = BeakerIcon;   const ResearchSolid = BeakerSolid;
const CompanyIcon  = BriefcaseIcon; const CompanySolid  = BriefcaseSolid;

// ═══════════════════════════════════════════════════════════════
//  PRODUCT MEGA-MENU DATA — original 4 categories restored
// ═══════════════════════════════════════════════════════════════

const PRODUCT_CHALLENGES = [
  {
    id: "cybersecurity",
    challenge: "Cyber Security",
    solutionLabel: "Cybersecurity Shield",
    accent: "#EF4444",
    tagline: "Stop threats before they become breaches — AI watches, learns, and responds 24/7",
    badge: "Most Critical",
    stats: [{ num: "99.7%", label: "Threat detection accuracy" }, { num: "<2 min", label: "Mean time to respond" }],
    solutions: [
      {
        name: "ONYXx Threat Guard",
        tagline: "AI catches what humans miss",
        desc: "Real-time AI threat detection · LLM-powered anomaly alerts · Zero-day response",
        route: "/onyxx",
        highlight: true,
      },
      {
        name: "Always-On SOC",
        tagline: "Your security team, 24/7",
        desc: "Managed SOC · Incident containment · Threat hunting",
        route: "/product/productCyber2",
      },
      {
        name: "Zero-Trust Access",
        tagline: "Trust nobody, verify everyone",
        desc: "MFA · Role-based access control · SSO · Privileged access",
        route: "/product/productCyber3",
      },
      {
        name: "Cloud Security Suite",
        tagline: "Your cloud, fully compliant",
        desc: "Multi-cloud posture management · GDPR/ISO compliance · Runtime protection",
        route: "/product/productCyber4",
      },
    ],
    explore: [
      { label: "See all Security solutions", route: "/services/cyber-security" },
      { label: "Get a free threat assessment", route: "/contact", cta: true },
    ],
  },
  {
    id: "data",
    challenge: "Data & Analytics",
    solutionLabel: "Business Intelligence Hub",
    accent: "#0AAAF0",
    tagline: "Turn scattered data into decisions — dashboards that update in real time",
    badge: "High ROI",
    stats: [{ num: "3×", label: "Faster decision cycles" }, { num: "40%", label: "Avg. cost reduction identified" }],
    solutions: [
      {
        name: "Executive Command Center",
        tagline: "See the whole business at a glance",
        desc: "Live KPI dashboards · Revenue tracking · Board-ready reports",
        route: "/product/productData1",
        highlight: true,
      },
      {
        name: "People & Workforce Lens",
        tagline: "Know your team like never before",
        desc: "Attrition risk · Performance analytics · Hiring funnel intelligence",
        route: "/product/productData2",
      },
      {
        name: "Finance Intelligence Engine",
        tagline: "Forecast with confidence",
        desc: "P&L analysis · Cash flow forecasting · Cost-centre drill-downs",
        route: "/product/productData3",
      },
      {
        name: "Risk Radar",
        tagline: "Catch fraud before it costs you",
        desc: "Anomaly detection · Compliance scoring · Fraud pattern recognition",
        route: "/product/productData4",
      },
    ],
    explore: [
      { label: "Explore Analytics solutions", route: "/services/ai-data-analytics" },
      { label: "Request a live demo", route: "/contact", cta: true },
    ],
  },
  {
    id: "ai",
    challenge: "Artificial Intelligence",
    solutionLabel: "AI Automation Suite",
    accent: "#8B5CF6",
    tagline: "Automate the repetitive, accelerate the important — your team gets time back to do real work",
    badge: "Biggest Time Saver",
    stats: [{ num: "70%", label: "Reduction in manual tasks" }, { num: "5×", label: "Faster document processing" }],
    solutions: [
      {
        name: "AI Workforce Assistant",
        tagline: "Your smartest team member",
        desc: "Task automation · Multi-channel deployment · Natural language workflows",
        route: "/product/productMl1",
        highlight: true,
      },
      {
        name: "Intelligent Document Processor",
        tagline: "Docs in, decisions out",
        desc: "OCR + AI extraction · Contract analysis · Auto-routing & approvals",
        route: "/product/productMl2",
      },
      {
        name: "Customer Intelligence Engine",
        tagline: "Personalise at scale",
        desc: "Behavioural analytics · AI recommendations · Churn prediction",
        route: "/product/productMl3",
      },
      {
        name: "Voice & Conversational AI",
        tagline: "Talk to your systems",
        desc: "Natural dialogue · Speech recognition · 40+ language support",
        route: "/product/productMl4",
      },
    ],
    explore: [
      { label: "Explore AI & ML solutions", route: "/services/ai-data-analytics" },
      { label: "Book an AI audit", route: "/contact", cta: true },
    ],
  },
  {
    id: "web",
    challenge: "Web",
    solutionLabel: "Digital Growth Platform",
    accent: "#10B981",
    tagline: "Websites and platforms that don't just look good — they sell, retain, and scale with you",
    badge: "Growth Engine",
    stats: [{ num: "2.4×", label: "Average conversion uplift" }, { num: "99.9%", label: "Uptime SLA guaranteed" }],
    solutions: [
      {
        name: "Commerce & Marketplace Engine",
        tagline: "Sell more, manage less",
        desc: "Multi-vendor platforms · AI-powered search · One-click checkout",
        route: "/product/productWeb1",
        highlight: true,
      },
      {
        name: "SaaS Builder Platform",
        tagline: "Launch your product faster",
        desc: "Multi-tenant architecture · Billing integrations · Scalable infra",
        route: "/product/productWeb2",
      },
      {
        name: "Learning & Training Portal",
        tagline: "Upskill your people at scale",
        desc: "Course management · Live sessions · Completion analytics",
        route: "/product/productWeb3",
      },
      {
        name: "Healthcare Patient Portal",
        tagline: "Care that's always accessible",
        desc: "HIPAA compliant · Patient records · Telemedicine · Scheduling",
        route: "/product/productWeb4",
      },
    ],
    explore: [
      { label: "View Web solutions", route: "/services/application-development" },
      { label: "Get a free quote", route: "/contact", cta: true },
    ],
  },
  {
    id: "mobile",
    challenge: "Mobile",
    solutionLabel: "Mobile Operations Suite",
    accent: "#F59E0B",
    tagline: "Put powerful tools in every pocket — native apps that keep operations running, everywhere",
    badge: "Field-Ready",
    stats: [{ num: "60%", label: "Faster order fulfilment" }, { num: "4.8★", label: "Avg. app store rating" }],
    solutions: [
      {
        name: "Supply Chain Control App",
        tagline: "Never lose track of inventory",
        desc: "Real-time stock · Vendor management · PO automation",
        route: "/product/productMobile1",
        highlight: true,
      },
      {
        name: "Last-Mile Delivery App",
        tagline: "Every package, on time",
        desc: "Live GPS tracking · Route optimisation · Proof of delivery",
        route: "/product/productMobile2",
      },
      {
        name: "Retail Commerce App",
        tagline: "Shopping that delights",
        desc: "Smooth checkout · Loyalty rewards · Push personalisation",
        route: "/product/productMobile3",
      },
      {
        name: "HR & Workforce App",
        tagline: "Manage people, not paperwork",
        desc: "Attendance · Payroll · Performance reviews · Leave management",
        route: "/product/productMobile4",
      },
    ],
    explore: [
      { label: "View Mobile solutions", route: "/services/application-development" },
      { label: "See app demos", route: "/contact", cta: true },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════
//  SERVICES CONFIG
// ═══════════════════════════════════════════════════════════════

const SERVICES_CONFIG = {
  tabs: [
    {
      id: "solutions",
      icon: AppIcon,
      iconSolid: AppSolid,
      label: "Solutions",
      tagline: "Engineering, cloud, AI & enterprise transformation",
      color: "#0AAAF0",
      route: "/services",
      sections: [
        {
          heading: "ENGINEERING & DIGITAL",
          items: [
            { icon: CodeBracketIcon, iconSolid: CodeBracketSolid, name: "Application Development", desc: "Web, mobile & enterprise application development", route: "/services/application-development" },
            { icon: CloudIcon, iconSolid: CloudIconSolid, name: "Cloud & Infrastructure", desc: "Cloud migration, DevOps, servers & infrastructure management", route: "/services/cloud-infrastructure" },
            { icon: ShieldCheckIcon, iconSolid: ShieldCheckSolid, name: "Cyber Security & Networks", desc: "VAPT, SOC, compliance, VPN & network security solutions", route: "/services/cyber-security" },
          ],
        },
        {
          heading: "ENTERPRISE & AI",
          items: [
            { icon: Square3Stack3DIcon, iconSolid: Square3Stack3DSolid, name: "Salesforce & CRM Solutions", desc: "Salesforce implementation, automation & CRM modernization", route: "/services/salesforce-services" },
            { icon: BoltIcon, iconSolid: BoltSolid, name: "AI, Data & Analytics", desc: "GenAI, BI dashboards, ML & enterprise data platforms", route: "/services/ai-data-analytics" },
            { icon: ComputerDesktopIcon, iconSolid: ComputerDesktopSolid, name: "Enterprise Transformation", desc: "ERP, workflow automation & legacy modernization", route: "/services/enterprise-solutions" },
          ],
        },
        {
          heading: "QUALITY & SUPPORT",
          items: [
            { icon: CheckCircleIcon, iconSolid: CheckCircleSolid, name: "QA & Managed Services", desc: "Testing, monitoring, maintenance & ongoing support", route: "/services/qa-services" },
          ],
        },
        {
          heading: "FEATURED",
          type: "featured",
          pill: "NEW",
          title: "GenAI Enterprise Adoption",
          desc: "Securely deploy AI and automation across your organization.",
          route: "/services",
          cta: "Learn more →",
        },
      ],
      explore: [
        { label: "All Services", route: "/services" },
        { label: "Contact Us", route: "/contact" },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════
//  R&D CONFIG
// ═══════════════════════════════════════════════════════════════

const RND_CONFIG = {
  tabs: [
    {
      id: "research", icon: ResearchIcon, iconSolid: ResearchSolid,
      label: "Research Areas",
      tagline: "Pushing the boundaries of AI, quantum and edge intelligence",
      color: "#0AAAF0", route: "/research",
      sections: [
        {
          heading: "RESEARCH AREAS",
          items: [
            { icon: BeakerIcon, iconSolid: BeakerSolid, name: "AI & Deep Learning", desc: "LLMs, multi-modal, autonomous systems", route: "/research/ai" },
            { icon: CpuChipIcon, iconSolid: CpuChipSolid, name: "Quantum Computing", desc: "Algorithms, cryptography, drug discovery", route: "/research/quantum" },
            { icon: CloudIcon, iconSolid: CloudIconSolid, name: "Edge Computing & IoT", desc: "Lightweight inference, edge AI", route: "/research/edge" },
            { icon: SignalIcon, iconSolid: SignalSolid, name: "Network Intelligence", desc: "5G/6G optimisation, intent-based", route: "/research/network" },
          ],
        },
        {
          heading: "PROGRAM STATS", type: "stats",
          stats: [
            { value: "8", label: "Active Patents", sub: "Filed and pending" },
            { value: "30+", label: "Researchers", sub: "Across 6 countries" },
            { value: "12", label: "R&D Programs", sub: "Active this year" },
          ],
        },
        {
          heading: "LATEST PUBLICATION", type: "featured",
          pill: "RESEARCH",
          title: "Federated LLM Fine-Tuning at Scale",
          desc: "A privacy-first approach to enterprise model adaptation without centralised data exposure.",
          route: "/whitepaper", cta: "Read white paper →",
        },
      ],
      explore: [
        { label: "All Research", route: "/research" },
        { label: "White Papers", route: "/whitepaper" },
        { label: "Partner with Us", route: "/contact" },
      ],
    },
    {
      id: "whitepaper", icon: DocumentTextIcon, iconSolid: DocumentTextSolid,
      label: "Whitepapers & Resources",
      tagline: "In-depth research, case studies, and thought leadership on solving enterprise challenges",
      color: "#0AAAF0", route: "/whitepaper",
      sections: [
        {
          heading: "RESOURCES",
          items: [
            { icon: DocumentTextIcon, iconSolid: DocumentTextSolid, name: "Whitepapers", desc: "In-depth research & thought leadership", route: "/whitepaper" },
            { icon: NewspaperIcon, iconSolid: NewspaperSolid, name: "News & Insights", desc: "Latest updates & industry articles", route: "/newslatter" },
           
          ],
        },
        {
          heading: "FEATURED PAPERS", type: "stats",
          stats: [
            { value: "8", label: "White Papers", sub: "Enterprise research" },
            { value: "24", label: "Publications", sub: "Peer-reviewed" },
            { value: "12", label: "Case Studies", sub: "Success stories" },
          ],
        },
        {
          heading: "LATEST INSIGHTS", type: "featured",
          pill: "NEW",
          title: "The State of Enterprise AI in 2025",
          desc: "How global enterprises are adopting generative AI to solve real business challenges.",
          route: "/whitepaper", cta: "Read white paper →",
        },
      ],
      explore: [
        { label: "All White Papers", route: "/whitepaper" },
        { label: "News & Articles", route: "/newslatter" },
        { label: "Contact Us", route: "/contact" },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════
//  COMPANY CONFIG
// ═══════════════════════════════════════════════════════════════

const COMPANY_CONFIG = {
  tabs: [
    {
      id: "about", icon: CompanyIcon, iconSolid: CompanySolid,
      label: "About",
      tagline: "Mission-driven technology company building enterprise solutions since 2012",
      color: "#0AAAF0", route: "/about",
      sections: [
        {
          heading: "COMPANY",
          items: [
            { icon: BriefcaseIcon, iconSolid: BriefcaseSolid, name: "About Cloudisian", desc: "Our story, mission & values", route: "/about" },
            { icon: NewspaperIcon, iconSolid: NewspaperSolid, name: "News & Insights", desc: "Latest updates & articles", route: "/newslatter" },
            { icon: PencilSquareIcon, iconSolid: PencilSquareSolid, name: "Blogs", desc: "Insights, guides & tech deep-dives", route: "/blogs" },
            { icon: EnvelopeIcon, iconSolid: EnvelopeSolid, name: "Contact Us", desc: "Get in touch with our team", route: "/contact" },
          ],
        },
        {
          heading: "BY THE NUMBERS", type: "stats",
          stats: [
            { value: "200+", label: "Enterprise Clients", sub: "Across 30+ industries" },
            { value: "12+", label: "Years in Business", sub: "Founded 2012" },
            { value: "6", label: "Global Offices", sub: "4 continents" },
          ],
        },
        {
          heading: "CONNECT", type: "featured",
          title: "Ready to Transform?",
          desc: "Talk to our experts and find the right solution for your business needs.",
          route: "/contact", cta: "Schedule a Call →",
          button: { label: "Get in Touch ↗", route: "/contact" },
        },
      ],
      explore: [
        { label: "About Us", route: "/about" },
        { label: "Blogs", route: "/blogs" },
      ],
    },
  ],
};

// ═══════════════════════════════════════════════════════════════
//  SHARED ICONS
// ═══════════════════════════════════════════════════════════════

function ChevronDown() {
  return (
    <svg className="ch-chevron" viewBox="0 0 12 12" fill="none">
      <path d="M2 4.5L6 8.5L10 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowRight() {
  return <ChevronRightIcon style={{ width: 13, height: 13, opacity: 0.5 }} />;
}

// ═══════════════════════════════════════════════════════════════
//  GENERIC DROP ITEM
// ═══════════════════════════════════════════════════════════════

function DropItem({ name, desc, route, onClose, navigate }) {
  return (
    <button
      className="ch-drop-item"
      onClick={() => { if (route) navigate(route); onClose(); }}
    >
      <div className="di-text">
        <p className="di-name">{name}</p>
        <p className="di-desc">{desc}</p>
      </div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
//  SOLUTION CARD — no icons
// ═══════════════════════════════════════════════════════════════

function SolutionCard({ solution, accent, onClose, navigate }) {
  return (
    <button
      className={`ps-card ${solution.highlight ? "ps-card--featured" : ""}`}
      style={solution.highlight ? { "--ps-accent": accent } : {}}
      onClick={() => { if (solution.route) navigate(solution.route); onClose(); }}
    >
      <span className="ps-body">
        <span className="ps-name">{solution.name}</span>
        <span className="ps-tagline">{solution.tagline}</span>
        <span className="ps-desc">{solution.desc}</span>
      </span>
      {solution.highlight && (
        <span className="ps-badge" style={{ background: accent }}>Top Pick</span>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
//  PRODUCT MEGA MENU
// ═══════════════════════════════════════════════════════════════

function ProductMegaMenu({ onClose, navigate }) {
  const col1 = [
    { name: "ONYXx AI Security", desc: "AI-powered threat detection & real-time response", route: "/onyxx", featured: true, badge: "Top Pick" },
    
    { name: "Vendor Management App", desc: "Centralise supplier onboarding, contracts & payments", route: "/product/vendor", featured: true, badge: "New" },
    { name: "Risk Radar", desc: "Fraud detection, anomaly scoring & compliance alerts", route: "/product/productData4" },
    { name: "Zero-Trust Access", desc: "MFA, SSO & role-based access control", route: "/product/productCyber3" },
    { name: "Always-On SOC", desc: "Managed SOC · Incident containment · Threat hunting", route: "/product/productCyber2"},
  ];
  const col2 = [
     { name: "AI Governance Evaluation", desc: "Evaluate your organization's AI governance, security, and AI adoption readiness", route: "/ai-assesment" ,featured: true, badge: "New"},
    { name: "Commerce & Marketplace Engine", desc: "Scalable marketplace operations with AI-powered search", route: "/product/productWeb1" },
    { name: "M&A Intelligence Suite", desc: "Due diligence automation, deal tracking & analytics", route: "/product/ma-suite" },
    { name: "HR & Workforce App", desc: "Attendance, payroll & performance — all in one app", route: "/product/productMobile4" },
    { name: "AI Workforce Assistant", desc: "Task automation · Multi-channel deployment · Natural language workflows", route: "/product/productMl1", },
  ];

  return (
    <div className="ch-product-mega ch-product-mega--omni">
      {/* LEFT PANEL */}
      <div className="ch-omni-left">
        <p className="ch-omni-eyebrow">Our Products</p>
        <h2 className="ch-omni-heading">Software built for how businesses actually work</h2>
        <p className="ch-omni-para">
          From AI-powered security to mobile workforce tools — every product we ship solves a real problem. 
          Explore what's possible when technology is built around your operations, not the other way around.
        </p>
        <a className="ch-omni-see-all" href="/product" onClick={(e) => { e.preventDefault(); navigate("/product"); onClose(); }}>
          SEE ALL PRODUCTS →
        </a>
      </div>

      {/* PRODUCT COLUMNS */}
      <div className="ch-omni-products">
        {[{ heading: "Security & Intelligence", items: col1 }, { heading: "Business Applications", items: col2 }].map((col) => (
          <div key={col.heading} className="ch-omni-col">
            <p className="ch-omni-col-head">{col.heading}</p>
            {col.items.map((p) => (
              <button
                key={p.name}
                className={`ch-omni-item ${p.featured ? "ch-omni-item--featured" : ""}`}
                onClick={() => { navigate(p.route); onClose(); }}
              >
                <span className="ch-omni-name">{p.name}</span>
                <span className="ch-omni-desc">{p.desc}</span>
                {p.badge && <span className="ch-omni-badge">{p.badge}</span>}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* FEATURED SIDE */}
      <div className="ch-omni-side">
        <div className="ch-omni-feat-card">
          <span className="ch-omni-feat-pill">Spotlight</span>
          <p className="ch-omni-feat-title">ONYXx — AI Security That Never Sleeps</p>
          <p className="ch-omni-feat-body">Detect zero-day threats before they breach. Our most advanced product, now with LLM-powered anomaly detection.</p>
          <a className="ch-omni-feat-link" href="/onyxx" onClick={(e) => { e.preventDefault(); navigate("/onyxx"); onClose(); }}>Read the case study →</a>
        </div>
        <div className="ch-omni-stats">
          {[{ num: "99.7%", label: "Threat detection accuracy" }, { num: "8 apps", label: "Ready to deploy today" }, { num: "200+", label: "Enterprise clients" }].map((s) => (
            <div key={s.label} className="ch-omni-stat">
              <span className="ch-omni-stat-num">{s.num}</span>
              <span className="ch-omni-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════
//  GENERIC MEGA MENU (Services, R&D, Company)
// ═══════════════════════════════════════════════════════════════

function GenericMegaMenu({ config, onClose, navigate }) {
  const [activeId, setActiveId] = useState(config.tabs[0].id);
  const active = config.tabs.find((t) => t.id === activeId);
  const hasSidebar = config.tabs.length > 1;

  return (
    <div className="ch-product-mega">
      {hasSidebar && (
        <div className="ch-pm-sidebar">
          {config.tabs.map((tab) => (
            <button
              key={tab.id}
              className={`ch-pm-sidebar-btn ${activeId === tab.id ? "active" : ""}`}
              onMouseEnter={() => setActiveId(tab.id)}
              onClick={() => { if (tab.route) navigate(tab.route); onClose(); }}
            >
              <span className="sb-left"><span>{tab.label}</span></span>
              <ArrowRight />
            </button>
          ))}
        </div>
      )}

      <div className="ch-pm-panel">
        <div className="ch-pm-banner" style={{ background: `linear-gradient(135deg,${active.color || "#0AAAF0"}18 0%,${active.color || "#0AAAF0"}09 100%)` }}>
          <span className="ch-pm-banner-text" style={{ color: active.color || "#0088CC" }}>{active.tagline}</span>
        </div>

        <div className="ch-pm-grid" style={{ gridTemplateColumns: `repeat(${active.sections.length}, 1fr)` }}>
          {active.sections.map((sec) => (
            <div key={sec.heading}>
              <p className="ch-pm-section-head">{sec.heading}</p>
              {sec.type === "stats" ? (
                <div className="ch-stats-col">
                  {sec.stats.map((s) => (
                    <div key={s.label} className="ch-stat-card">
                      <span className="ch-stat-num">{s.value}</span>
                      <div className="di-text">
                        <p className="di-name">{s.label}</p>
                        {s.sub && <p className="di-desc">{s.sub}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : sec.type === "featured" ? (
                <div className="ch-featured-card">
                  {sec.pill && <span className="ch-fc-pill">{sec.pill}</span>}
                  <h4 className="ch-fc-title">{sec.title}</h4>
                  <p className="ch-fc-desc">{sec.desc}</p>
                  <a className="ch-fc-link" href={sec.route} onClick={(e) => { e.preventDefault(); navigate(sec.route); onClose(); }}>
                    {sec.cta || "Learn more →"}
                  </a>
                  {sec.button && (
                    <a className="ch-btn-cta ch-fc-btn" href={sec.button.route} onClick={(e) => { e.preventDefault(); navigate(sec.button.route); onClose(); }}>
                      {sec.button.label}
                    </a>
                  )}
                </div>
              ) : (
                <div className="ch-pm-items">
                  {sec.items.map((item) => (
                    <DropItem key={item.name} {...item} onClose={onClose} navigate={navigate} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="ch-pm-footer">
          <span>Explore</span>
          {active.explore.map((ex, i) => (
            <span key={ex.label} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {i > 0 && <span className="ch-pm-footer-sep">|</span>}
              <a href={ex.route} style={{ color: active.color || "#0088CC" }} onClick={(e) => { e.preventDefault(); navigate(ex.route); onClose(); }}>
                {ex.label}
              </a>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  MOBILE PRODUCT ACCORDION
// ═══════════════════════════════════════════════════════════════

function MobileProductAccordion({ onClose, navigate }) {
  const [expandedId, setExpandedId] = useState(null);

  const toggle = (id) => setExpandedId(expandedId === id ? null : id);

  return (
    <div className="ch-mobile-product-accordion">
      {PRODUCT_CHALLENGES.map((cat) => {
        const isOpen = expandedId === cat.id;
        return (
          <div key={cat.id} className="ch-mobile-acc-item">
            <button
              className={`ch-mobile-acc-header ${isOpen ? "open" : ""}`}
              style={isOpen ? { color: cat.accent, borderLeftColor: cat.accent } : {}}
              onClick={() => toggle(cat.id)}
            >
              <span>{cat.challenge}</span>
              <svg className={`ch-mobile-acc-chevron ${isOpen ? "rotated" : ""}`} viewBox="0 0 12 12" fill="none">
                <path d="M2 4.5L6 8.5L10 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {isOpen && (
              <div className="ch-mobile-acc-body">
                {cat.solutions.map((sol) => (
                  <button
                    key={sol.name}
                    className={`ch-mobile-sol-item ${sol.highlight ? "featured" : ""}`}
                    style={sol.highlight ? { borderLeftColor: cat.accent } : {}}
                    onClick={() => { navigate(sol.route); onClose(); }}
                  >
                    <span className="ch-mobile-sol-name">{sol.name}</span>
                    <span className="ch-mobile-sol-tagline">{sol.tagline}</span>
                  </button>
                ))}
                <div className="ch-mobile-acc-footer">
                  {cat.explore.map((ex) => (
                    <a
                      key={ex.label}
                      href={ex.route}
                      className={ex.cta ? "ch-mobile-cta-link" : "ch-mobile-plain-link"}
                      style={ex.cta ? { background: cat.accent } : { color: cat.accent }}
                      onClick={(e) => { e.preventDefault(); navigate(ex.route); onClose(); }}
                    >
                      {ex.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  MAIN HEADER
// ═══════════════════════════════════════════════════════════════

export default function Header() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null); // null | 'products' | 'other'
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const close = useCallback(() => setOpenMenu(null), []);
  const closeAll = useCallback(() => { setOpenMenu(null); setMobileOpen(false); setMobileSection(null); }, []);

  useEffect(() => {
    const handler = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [close]);

  useEffect(() => { closeAll(); }, [location.pathname, closeAll]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  const navItems = [
    { label: "Products", key: "product" },
    { label: "Services", key: "services" },
    { label: "R&D",      key: "rnd" },
    { label: "Resource", key: "company" },
  ];

  const megaMenuKeys = ["product", "services", "rnd", "company"];

  return (
    <>
      <div className={`ch-backdrop ${openMenu ? "visible" : ""}`} onClick={close} />

      <header ref={headerRef} className="cloudisian-header">
        <div className="ch-bar">
          <div className="ch-inner">
            <a className="ch-logo" href="/" onClick={(e) => { e.preventDefault(); navigate("/"); closeAll(); }} aria-label="Cloudisian home">
              <img className="ch-logo-only" src={CloudisianLogo} alt="Cloudisian" />
            </a>

            {/* Desktop nav */}
            <nav className="ch-nav" aria-label="Main navigation">
              {navItems.map(({ label, key }) => (
                <div className="ch-nav-item" key={key}>
                  <button
                    className={`ch-nav-btn ${openMenu === key ? "is-open" : ""}`}
                    onMouseEnter={() => setOpenMenu(key)}
                    aria-expanded={openMenu === key}
                    aria-haspopup="true"
                  >
                    {label}
                    <ChevronDown />
                  </button>
                </div>
              ))}
              <a
                className="ch-nav-btn ch-nav-careers"
                href="/careers"
                onClick={(e) => { e.preventDefault(); navigate("/careers"); closeAll(); }}
              >
                Careers
              </a>
            </nav>

            <div className="ch-right">
              <a className="ch-btn-ghost" href="/contact" onClick={(e) => { e.preventDefault(); navigate("/onyxxform"); }}>Book Free Trail</a>
              <a className="ch-btn-cta" href="/contact" onClick={(e) => { e.preventDefault(); navigate("/contact"); }}>Get in Touch ↗</a>
              <button
                type="button"
                className={`ch-mobile-toggle ${mobileOpen ? "open" : ""}`}
                onClick={() => { setMobileOpen((o) => !o); setMobileSection(null); }}
                aria-label="Open navigation"
                aria-expanded={mobileOpen}
              >
                <span /><span /><span />
              </button>
            </div>

            {/* Desktop mega menus */}
            {megaMenuKeys.includes(openMenu) && (
              <div className="ch-mega-anchor" onMouseLeave={close}>
                {openMenu === "product"  && <ProductMegaMenu onClose={close} navigate={navigate} />}
                {openMenu === "services" && <GenericMegaMenu config={SERVICES_CONFIG} onClose={close} navigate={navigate} />}
                {openMenu === "rnd"      && <GenericMegaMenu config={RND_CONFIG}      onClose={close} navigate={navigate} />}
                {openMenu === "company"  && <GenericMegaMenu config={COMPANY_CONFIG}  onClose={close} navigate={navigate} />}
              </div>
            )}
          </div>

          {/* Mobile menu */}
          {mobileOpen && (
            <div className="ch-mobile-menu">
              <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); closeAll(); }}>Home</a>

              {/* Products — expandable accordion in mobile */}
              <div className="ch-mobile-section">
                <button
                  className={`ch-mobile-section-toggle ${mobileSection === "products" ? "open" : ""}`}
                  onClick={() => setMobileSection(mobileSection === "products" ? null : "products")}
                >
                  <span>Products</span>
                  <svg className={`ch-mobile-acc-chevron ${mobileSection === "products" ? "rotated" : ""}`} viewBox="0 0 12 12" fill="none">
                    <path d="M2 4.5L6 8.5L10 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {mobileSection === "products" && (
                  <MobileProductAccordion onClose={closeAll} navigate={navigate} />
                )}
              </div>

              <a href="/services" onClick={(e) => { e.preventDefault(); navigate("/services"); closeAll(); }}>Services</a>
              <a href="/research" onClick={(e) => { e.preventDefault(); navigate("/research"); closeAll(); }}>R&D</a>
              <a href="/blogs"    onClick={(e) => { e.preventDefault(); navigate("/blogs");    closeAll(); }}>Blogs</a>
              <a href="/careers"  onClick={(e) => { e.preventDefault(); navigate("/careers");  closeAll(); }}>Careers</a>
              <a href="/contact"  onClick={(e) => { e.preventDefault(); navigate("/contact");  closeAll(); }}>Contact</a>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
